import type { NavigateFunction } from 'react-router-dom'
import {
  instrumentCreatePath,
  instrumentCreateTemplatesPath,
  instrumentQuickCreatePath,
  publishWizardBackPath,
  instrumentRoutingBackPath,
  reviewPublishPath,
} from '@/features/instruments/lib/instrumentNavigation'
import {
  isQuickInstrumentCreateSession,
  readInstrumentCreateContext,
} from '@/features/instruments/lib/instrumentCreateSession'
import {
  getPublishDraftSnapshot,
  usePublishStore,
} from '@/stores/usePublishStore'
import { patchPublishDraft } from '@/features/instruments/publish/shared/api/publish-draft.api'

export type InstrumentCreateBackFlags = Readonly<{
  isInstrumentCreateTemplates: boolean
  isInstrumentEditInfo: boolean
  isInstrumentCreateInfo: boolean
}>

function createPickerBackPath(): string {
  const createCtx = readInstrumentCreateContext()
  if (isQuickInstrumentCreateSession(createCtx)) {
    return instrumentQuickCreatePath()
  }
  if (createCtx?.campaignId?.trim() && createCtx.loiId?.trim()) {
    return instrumentCreatePath(createCtx.campaignId, createCtx.loiId)
  }
  return '/instruments/create'
}

export function runInstrumentCreateBack(
  navigate: NavigateFunction,
  flags: InstrumentCreateBackFlags,
): void {
  const createCtx = readInstrumentCreateContext()
  if (flags.isInstrumentCreateTemplates) {
    navigate(createPickerBackPath())
    return
  }
  if (flags.isInstrumentEditInfo || flags.isInstrumentCreateInfo) {
    if (createCtx?.templateId || createCtx?.builtInInstrumentId) {
      navigate(instrumentCreateTemplatesPath())
      return
    }
    navigate(createPickerBackPath())
    return
  }
  if (isQuickInstrumentCreateSession(createCtx)) {
    navigate('/campaigns?tab=direct')
    return
  }
  const campaignId = createCtx?.campaignId ?? ''
  navigate(campaignId ? `/campaigns/${campaignId}` : '/campaigns')
}

export type InstrumentWorkspaceBackArgs = Readonly<{
  isPublish: boolean
  isInstrumentRouting: boolean
  workspaceInstrumentId: string
  campaignId: string
  returnToPreview: boolean
  pathname: string
  navigate: NavigateFunction
  onQuestionBuilderBack: () => void
}>

export function flushPublishDraftBeforeLeave(workspaceInstrumentId: string): void {
  if (!workspaceInstrumentId) return
  const snapshot = getPublishDraftSnapshot()
  void patchPublishDraft(workspaceInstrumentId, {
    ...snapshot,
    wizardStep: usePublishStore.getState().maxReachedStep,
  })
}

export function runInstrumentWorkspaceBack(args: InstrumentWorkspaceBackArgs): void {
  const {
    isPublish,
    isInstrumentRouting,
    workspaceInstrumentId,
    returnToPreview,
    pathname,
    navigate,
    onQuestionBuilderBack,
  } = args

  if (isPublish && workspaceInstrumentId) {
    // BFP-999 — publish routes are instrument-scoped; empty campaignId is valid for quick.
    flushPublishDraftBeforeLeave(workspaceInstrumentId)
    navigate(publishWizardBackPath(pathname, workspaceInstrumentId))
    return
  }
  if (isInstrumentRouting && workspaceInstrumentId) {
    if (returnToPreview) {
      navigate(reviewPublishPath(workspaceInstrumentId))
      return
    }
    navigate(instrumentRoutingBackPath(workspaceInstrumentId))
    return
  }
  onQuestionBuilderBack()
}
